/** @module @liquidefy/code-editor/ai/prompts/templates @version 1.0.0 @license LQD Research LLC */
/** @rust code_editor_ai_prompts_templates.rs — false; 4/2/26; sm */

// ═══════════════════════════════════════════════════════════════════════════════
// PROMPT TEMPLATE ENGINE — Named templates, variable interpolation, composition,
// message construction, token-budget fitting, few-shot injection.
// All functions pure. No side effects. No API calls.
// ═══════════════════════════════════════════════════════════════════════════════

import type { AIRequestType } from '../../code-editor-types.js';

// ─── Types ───────────────────────────────────────────────────────────────────

export type MessageRole = 'system' | 'user' | 'assistant';

export interface PromptMessage {
  readonly role: MessageRole;
  readonly content: string;
}

export interface PromptTemplate {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly messages: readonly PromptMessage[];
  readonly variables: readonly string[];
  readonly expectedOutputFormat?: 'text' | 'code' | 'json' | 'markdown';
  readonly tokenEstimate: number;
}

export interface CompiledPrompt {
  readonly messages: readonly PromptMessage[];
  readonly totalTokens: number;
  readonly variables: Readonly<Record<string, string>>;
  readonly templateId: string;
  readonly truncated: boolean;
}

export interface TemplateRegistry {
  readonly templates: Readonly<Record<string, PromptTemplate>>;
}

// ─── Token estimation ────────────────────────────────────────────────────────

const CODE_TOKEN_RATIO = 3.2;
const PROSE_TOKEN_RATIO = 4.0;

/**
 * Estimate token count for a string. Code is denser (more tokens per char)
 * than prose due to operators, punctuation, identifiers.
 */
export function estimateTokens(text: string): number {
  if (text.length === 0) return 0;
  const codeSignals = (text.match(/[{}();=<>[\]|&!+\-*/]/g) || []).length;
  const ratio = codeSignals > text.length * 0.05 ? CODE_TOKEN_RATIO : PROSE_TOKEN_RATIO;
  return Math.ceil(text.length / ratio);
}

function estimateMessagesTokens(messages: readonly PromptMessage[]): number {
  let total = 0;
  for (const m of messages) {
    total += estimateTokens(m.content) + 4; // role overhead per message
  }
  return total;
}

// ─── Variable interpolation ──────────────────────────────────────────────────

const VARIABLE_PATTERN = /\{\{(\w+)\}\}/g;

/** Replace {{key}} with values. Unmatched variables stay as-is. */
export function interpolate(text: string, variables: Readonly<Record<string, string>>): string {
  return text.replace(VARIABLE_PATTERN, (match, key: string) => {
    return Object.prototype.hasOwnProperty.call(variables, key) ? variables[key]! : match;
  });
}

/** Find all {{key}} patterns in text. */
export function extractVariables(text: string): string[] {
  const vars: string[] = [];
  let match: RegExpExecArray | null;
  const re = new RegExp(VARIABLE_PATTERN.source, 'g');
  while ((match = re.exec(text)) !== null) {
    const name = match[1]!;
    if (!vars.includes(name)) vars.push(name);
  }
  return vars;
}

/** Check that all required template variables are supplied. */
export function validateVariables(
  template: PromptTemplate,
  variables: Readonly<Record<string, string>>,
): { valid: boolean; missing: string[]; extra: string[] } {
  const supplied = Object.keys(variables);
  const required = template.variables as readonly string[];
  const missing = required.filter(v => !supplied.includes(v));
  const extra = supplied.filter(v => !(required as readonly string[]).includes(v));
  return { valid: missing.length === 0, missing, extra };
}

// ─── Message factories ──────────────────────────────────────────────────────

export function createSystemMessage(content: string): PromptMessage {
  return { role: 'system', content };
}

export function createUserMessage(content: string): PromptMessage {
  return { role: 'user', content };
}

export function createAssistantMessage(content: string): PromptMessage {
  return { role: 'assistant', content };
}

/** Format messages as "role: content\n\n" blocks. */
export function messagesToString(messages: readonly PromptMessage[]): string {
  return messages.map(m => `${m.role}: ${m.content}`).join('\n\n');
}

// ─── Token budget fitting ────────────────────────────────────────────────────

/**
 * Fit messages into a token budget. Strategy: keep system message and first/last
 * user messages intact. Trim middle messages. If still over budget, truncate the
 * longest content from the middle of the remaining messages.
 */
export function fitToTokenBudget(
  messages: readonly PromptMessage[],
  maxTokens: number,
): { messages: PromptMessage[]; truncated: boolean } {
  const current = estimateMessagesTokens(messages);
  if (current <= maxTokens) {
    return { messages: [...messages], truncated: false };
  }

  // Phase 1: identify anchors (system msgs, first user, last user)
  const anchored = new Set<number>();
  for (let i = 0; i < messages.length; i++) {
    if (messages[i]!.role === 'system') anchored.add(i);
  }
  const firstUser = messages.findIndex(m => m.role === 'user');
  const lastUser = findLastIndex(messages, m => m.role === 'user');
  if (firstUser >= 0) anchored.add(firstUser);
  if (lastUser >= 0 && lastUser !== firstUser) anchored.add(lastUser);

  // Phase 2: drop non-anchored messages from the middle
  let kept = messages.filter((_, i) => anchored.has(i));
  let tokens = estimateMessagesTokens(kept);
  if (tokens <= maxTokens) {
    return { messages: kept, truncated: true };
  }

  // Phase 3: truncate the longest message content
  const mutable = kept.map(m => ({ ...m }));
  while (tokens > maxTokens && mutable.length > 0) {
    let longestIdx = 0;
    let longestLen = 0;
    for (let i = 0; i < mutable.length; i++) {
      if (mutable[i]!.content.length > longestLen) {
        longestLen = mutable[i]!.content.length;
        longestIdx = i;
      }
    }
    const msg = mutable[longestIdx]!;
    const targetChars = Math.max(100, Math.floor(msg.content.length * 0.6));
    const half = Math.floor(targetChars / 2);
    const truncated = msg.content.slice(0, half) + '\n... [truncated] ...\n' + msg.content.slice(-half);
    mutable[longestIdx] = { role: msg.role, content: truncated };
    tokens = estimateMessagesTokens(mutable);
  }

  return { messages: mutable, truncated: true };
}

function findLastIndex<T>(arr: readonly T[], pred: (v: T) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (pred(arr[i]!)) return i;
  }
  return -1;
}

// ─── Template compilation ────────────────────────────────────────────────────

/** Substitute variables, estimate tokens, truncate if over budget. */
export function compileTemplate(
  template: PromptTemplate,
  variables: Readonly<Record<string, string>>,
  maxTokens?: number,
): CompiledPrompt {
  const compiled = template.messages.map(m => ({
    role: m.role,
    content: interpolate(m.content, variables),
  }));

  const totalTokens = estimateMessagesTokens(compiled);

  if (maxTokens !== undefined && totalTokens > maxTokens) {
    const fitted = fitToTokenBudget(compiled, maxTokens);
    return {
      messages: fitted.messages,
      totalTokens: estimateMessagesTokens(fitted.messages),
      variables,
      templateId: template.id,
      truncated: fitted.truncated,
    };
  }

  return {
    messages: compiled,
    totalTokens,
    variables,
    templateId: template.id,
    truncated: false,
  };
}

// ─── Template composition & mutation ─────────────────────────────────────────

/** Merge two templates: extension messages append after base messages. */
export function composeTemplates(base: PromptTemplate, extension: PromptTemplate): PromptTemplate {
  const mergedMessages = [...base.messages, ...extension.messages];
  const allVars = [...base.variables];
  for (const v of extension.variables) {
    if (!allVars.includes(v)) allVars.push(v);
  }
  return {
    id: `${base.id}+${extension.id}`,
    name: `${base.name} + ${extension.name}`,
    description: `${base.description} | ${extension.description}`,
    messages: mergedMessages,
    variables: allVars,
    expectedOutputFormat: extension.expectedOutputFormat ?? base.expectedOutputFormat,
    tokenEstimate: estimateMessagesTokens(mergedMessages),
  };
}

/** Insert user/assistant example pairs before the final user message. */
export function addFewShotExamples(
  template: PromptTemplate,
  examples: readonly { input: string; output: string }[],
): PromptTemplate {
  if (examples.length === 0) return template;
  const msgs = [...template.messages];
  const insertIdx = Math.max(0, findLastIndex(msgs, m => m.role === 'user'));
  const exampleMsgs: PromptMessage[] = [];
  for (const ex of examples) {
    exampleMsgs.push(createUserMessage(ex.input));
    exampleMsgs.push(createAssistantMessage(ex.output));
  }
  msgs.splice(insertIdx, 0, ...exampleMsgs);
  const allContent = msgs.map(m => m.content).join(' ');
  const newVars = extractVariables(allContent);
  return {
    ...template,
    messages: msgs,
    variables: newVars,
    tokenEstimate: estimateMessagesTokens(msgs),
  };
}

/** Add context string to the system message (appended). */
export function prependContext(template: PromptTemplate, context: string): PromptTemplate {
  const msgs = template.messages.map(m => {
    if (m.role === 'system') {
      return createSystemMessage(m.content + '\n\n' + context);
    }
    return m;
  });
  // If no system message exists, prepend one
  if (!msgs.some(m => m.role === 'system')) {
    msgs.unshift(createSystemMessage(context));
  }
  return {
    ...template,
    messages: msgs,
    tokenEstimate: estimateMessagesTokens(msgs),
  };
}

/** Set the expected output format. */
export function setOutputFormat(
  template: PromptTemplate,
  format: 'text' | 'code' | 'json' | 'markdown',
): PromptTemplate {
  return { ...template, expectedOutputFormat: format };
}

/** Estimate compiled size (with variable values). */
export function templateTokenEstimate(
  template: PromptTemplate,
  variables: Readonly<Record<string, string>>,
): number {
  let total = template.tokenEstimate;
  for (const key of template.variables) {
    if (Object.prototype.hasOwnProperty.call(variables, key)) {
      total += estimateTokens(variables[key]!);
    }
  }
  return total;
}

/** Clone a template with a new id. */
export function cloneTemplate(template: PromptTemplate, newId: string): PromptTemplate {
  return { ...template, id: newId };
}

// ─── Template Registry ──────────────────────────────────────────────────────

export function registerTemplate(registry: TemplateRegistry, template: PromptTemplate): TemplateRegistry {
  return {
    templates: { ...registry.templates, [template.id]: template },
  };
}

export function getTemplate(registry: TemplateRegistry, id: string): PromptTemplate | null {
  return registry.templates[id] ?? null;
}

export function listTemplates(registry: TemplateRegistry): PromptTemplate[] {
  return Object.values(registry.templates);
}

// ─── Built-in template definitions ──────────────────────────────────────────

function buildTemplate(
  id: string,
  name: string,
  description: string,
  messages: PromptMessage[],
  format?: 'text' | 'code' | 'json' | 'markdown',
): PromptTemplate {
  const allContent = messages.map(m => m.content).join(' ');
  return {
    id,
    name,
    description,
    messages,
    variables: extractVariables(allContent),
    expectedOutputFormat: format,
    tokenEstimate: estimateMessagesTokens(messages),
  };
}

// Compact builtin template definitions: [id, name, desc, sysMsg, userMsg, format]
type TemplateTuple = [string, string, string, string, string, PromptTemplate['expectedOutputFormat']];

const BUILTIN_DEFS: TemplateTuple[] = [
  ['completion', 'Code Completion', 'Complete code at cursor position',
    'You are a code completion assistant. Complete the code naturally, matching the existing style and conventions. Return ONLY the completion text — no explanation, no markdown fences.',
    'Complete the code:\n{{prefix}}{{cursor}}{{suffix}}', 'code'],
  ['analysis', 'Code Analysis', 'Analyze code for quality, complexity, security, or performance',
    'You are a code analysis expert. Analyze code thoroughly and return structured findings. Categorize issues by severity (critical, warning, info). Cite line numbers when possible.',
    'Analyze this code for {{analysisType}}:\n\n{{code}}', 'json'],
  ['generation', 'Code Generation', 'Generate code from a specification with context',
    'Generate code based on the specification. Match the project style visible in context. Return only valid, runnable code. Include necessary imports.',
    '{{instruction}}\n\nContext:\n{{context}}', 'code'],
  ['refactor', 'Code Refactoring', 'Refactor code preserving behavior',
    'Refactor the following code. Preserve all existing behavior. Improve readability, reduce duplication, and apply best practices. Return the full refactored code.',
    '{{code}}\n\nRefactoring goal: {{goal}}', 'code'],
  ['explain', 'Code Explanation', 'Explain code clearly for the reader',
    'Explain the code clearly and concisely. Cover what it does, how it works, and why key decisions were made. Use markdown formatting.',
    'Explain this code:\n\n{{code}}', 'markdown'],
  ['fix', 'Code Fix', 'Fix a code issue given error diagnostics',
    'Fix the code issue described below. Return the corrected code with a brief comment explaining the fix. Do not change unrelated code.',
    'Code:\n{{code}}\n\nError:\n{{error}}', 'code'],
  ['test-generate', 'Test Generation', 'Generate comprehensive tests',
    'Generate comprehensive tests for the provided code. Cover happy paths, edge cases, and error conditions. Infer the test framework from imports. Include setup/teardown as needed.',
    'Generate tests for:\n{{code}}\n\nFramework: {{framework}}', 'code'],
  ['document', 'Documentation Generation', 'Generate documentation (JSDoc, docstrings, etc.)',
    'Generate documentation for the provided code. Use the appropriate format for the language. Document parameters, return types, exceptions, and examples.',
    'Document:\n{{code}}', 'code'],
  ['review', 'Code Review', 'Review code for issues, style, and improvements',
    'Review this code thoroughly. Identify bugs, security issues, performance concerns, and style improvements. Organize feedback into sections: Issues, Suggestions, Positive Observations.',
    '{{code}}\n\nFocus on: {{focus}}', 'markdown'],
  ['conversation', 'Coding Assistant', 'General conversation about code with context',
    'You are a helpful coding assistant with access to the user\'s workspace context. Answer questions, explain concepts, and provide code examples. Be concise but thorough.',
    '{{message}}', 'markdown'],
  ['suggestion', 'Code Suggestion', 'Suggest improvements or alternatives',
    'Suggest improvements to the provided code. Offer concrete alternatives with brief explanations. Rank suggestions by impact. Return as a structured list.',
    'Suggest improvements for:\n{{code}}\n\nGoal: {{goal}}', 'json'],
  ['optimize', 'Code Optimization', 'Optimize code for performance, memory, or bundle size',
    'Optimize the following code. Focus on the specified optimization target. Preserve correctness. Explain each optimization with its expected impact.',
    '{{code}}\n\nOptimize for: {{target}}', 'code'],
  ['translate', 'Code Translation', 'Translate code from one language to another',
    'Translate the code to the target language. Use idiomatic patterns. Preserve logic and behavior. Add comments where the translation is non-obvious.',
    'Translate to {{targetLanguage}}:\n\n{{code}}', 'code'],
];

/** Create a TemplateRegistry pre-loaded with built-in templates for every AIRequestType. */
export function createTemplateRegistry(): TemplateRegistry {
  const templates: Record<string, PromptTemplate> = {};
  for (const [id, name, desc, sys, usr, fmt] of BUILTIN_DEFS) {
    templates[id] = buildTemplate(id, name, desc, [createSystemMessage(sys), createUserMessage(usr)], fmt);
  }
  return { templates };
}

/** Check whether a registry contains a template with the given id. */
export function hasTemplate(registry: TemplateRegistry, id: string): boolean {
  return id in registry.templates;
}

/** Remove a template from the registry (returns new registry). */
export function removeTemplate(registry: TemplateRegistry, id: string): TemplateRegistry {
  const next = { ...registry.templates };
  delete next[id];
  return { templates: next };
}

/** Count the number of messages in a template. */
export function messageCount(template: PromptTemplate): number {
  return template.messages.length;
}

/** Get the system message content from a template, or empty string if none. */
export function systemContent(template: PromptTemplate): string {
  const sys = template.messages.find(m => m.role === 'system');
  return sys?.content ?? '';
}
